import { useCallback, useState } from "react";
import { mappingConfigApi } from "@services/mappingConfigApi";
import type {
  ActivateBody,
  CreateDraftBody,
  DraftResponse,
  ImportDraftBody,
  ImportResponse,
  PatchDraftBody,
  PluginConfigVersion,
} from "@models/mappingConfig";

export interface Mutation<A extends unknown[], T> {
  /** Runs the call; rejects with the API error so the caller can branch on 409 / 422. */
  run: (...args: A) => Promise<T>;
  loading: boolean;
  error: unknown;
  reset: () => void;
}

export const useMutation = <A extends unknown[], T>(call: (...args: A) => Promise<T>): Mutation<A, T> => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const run = useCallback(
    async (...args: A) => {
      setLoading(true);
      setError(null);
      try {
        return await call(...args);
      } catch (err) {
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [call],
  );
  const reset = useCallback(() => setError(null), []);
  return { run, loading, error, reset };
};

export const useCreateDraft = (service: string, pluginId: string): Mutation<[CreateDraftBody], DraftResponse> =>
  useMutation(useCallback((body: CreateDraftBody) => mappingConfigApi.createDraft(service, pluginId, body), [service, pluginId]));

export const usePatchDraft = (service: string, pluginId: string): Mutation<[string, PatchDraftBody], DraftResponse> =>
  useMutation(
    useCallback(
      (versionId: string, body: PatchDraftBody) => mappingConfigApi.patchDraft(service, pluginId, versionId, body),
      [service, pluginId],
    ),
  );

export const useImportDraft = (service: string, pluginId: string): Mutation<[ImportDraftBody], ImportResponse> =>
  useMutation(useCallback((body: ImportDraftBody) => mappingConfigApi.importDraft(service, pluginId, body), [service, pluginId]));

export const useActivateVersion = (service: string, pluginId: string): Mutation<[string, ActivateBody], PluginConfigVersion> =>
  useMutation(
    useCallback(
      (versionId: string, body: ActivateBody) => mappingConfigApi.activate(service, pluginId, versionId, body),
      [service, pluginId],
    ),
  );

export const useDeleteDraft = (service: string, pluginId: string): Mutation<[string], void> =>
  useMutation(useCallback((versionId: string) => mappingConfigApi.deleteDraft(service, pluginId, versionId), [service, pluginId]));
